import { parseJsonArray } from '../../(common-lib)/Utils';

export const GOVERNMENT_ISSUED_ID_SECTION_ID = 'upload_government_issued_id';
export const GOVERNMENT_ISSUED_ID_DOC_ID = 'government_issued_id_doc';
export const ENTITY_PARTICIPANT_STORAGE_ID = 'entity_participant_items';
export const GOVERNMENT_ISSUED_ID_SECTION_TITLE = 'Government-Issued ID';
export const GOVERNMENT_ISSUED_ID_TEMPLATE_NAME = 'Upload Government-Issued ID';

export const initializeGovtIssuedIdDocs = <T>(
  sectionId: string,
  loanData: any,
  docIdToUploadedDoc: Record<string, T | undefined>,
  getDocument: (loanDataValue: any, documentId: string) => T | undefined
): boolean => {
  if (sectionId !== GOVERNMENT_ISSUED_ID_SECTION_ID) {
    return false;
  }

  const rawLoanData = typeof loanData?.getRawLoanData === 'function' ? loanData.getRawLoanData() : loanData;
  const participantsRaw = rawLoanData?.borrowerInformation?.[ENTITY_PARTICIPANT_STORAGE_ID];
  const participantCount = getParticipantCount(participantsRaw);

  for (let i = 0; i < participantCount; i += 1) {
    const derivedDocId = deriveGovtIssuedIdDocId(i);
    docIdToUploadedDoc[derivedDocId] = getDocument(loanData, derivedDocId);
  }
  return true;
};

export const buildGovtIssuedIdDocTypes = (
  sectionId: string,
  fieldIdToValue: Record<string, any>,
  loanData: any,
  baseSectionDocuments: any[]
): { docs: any[] } | undefined => {
  if (sectionId !== GOVERNMENT_ISSUED_ID_SECTION_ID) {
    return undefined;
  }

  const participantsRaw = fieldIdToValue[ENTITY_PARTICIPANT_STORAGE_ID]
    ?? loanData?.getRawLoanData?.()?.borrowerInformation?.[ENTITY_PARTICIPANT_STORAGE_ID];
  const participants = parseJsonArray<any>(participantsRaw);
  const participantCount = getParticipantCount(participantsRaw);

  const template = baseSectionDocuments.find((doc) => doc.documentId === GOVERNMENT_ISSUED_ID_DOC_ID)
    ?? baseSectionDocuments[0];
  if (!template) return undefined;

  const docs = Array.from({ length: participantCount }).map((_, index) => {
    const derivedId = deriveGovtIssuedIdDocId(index);
    const titleSuffix = buildParticipantTitleSuffix(index, participants[index]);

    return {
      ...template,
      id: derivedId,
      documentId: derivedId,
      name: `${template.name} - ${titleSuffix}`,
      title: `${template.name} - ${titleSuffix}`,
    };
  });

  return { docs };
};

export const buildGovtIssuedDocs = <TUploaded, TItem>(
  docIdToUploadedDoc: Record<string, TUploaded | undefined>,
  buildItem?: (docId: string, uploadedDoc: TUploaded) => TItem
): TItem[] => {
  return Object.entries(docIdToUploadedDoc)
    .filter(([docId, value]) => Boolean(value) && isGovtIssuedIdDerivedDocId(docId))
    .map(([docId, value]) => {
      const uploadedDoc = value as TUploaded;
      if (buildItem) {
        return buildItem(docId, uploadedDoc);
      }

      const blobName = (uploadedDoc as any).uniqueID || (uploadedDoc as any).id || docId;
      return {
        id: docId,
        name: (uploadedDoc as any).fileName || 'Government-Issued ID',
        fileName: (uploadedDoc as any).fileName || '',
        uploadDate: (uploadedDoc as any).uploadDate || new Date().toISOString(),
        fileType: (uploadedDoc as any).fileType || 'application/octet-stream',
        documentId: docId,
        section: GOVERNMENT_ISSUED_ID_SECTION_ID,
        sectionTitle: GOVERNMENT_ISSUED_ID_SECTION_TITLE,
        blobName,
        templateName: GOVERNMENT_ISSUED_ID_TEMPLATE_NAME,
      } as TItem;
    });
};

export const pickGovtIssuedIdDoc = <T>(
  baseDocId: string,
  docs: T[],
  options: { hasSuffix: boolean; suffixNumber: number }
): T | undefined => {
  if (baseDocId !== GOVERNMENT_ISSUED_ID_DOC_ID) return undefined;
  return pickGovtIssuedIdDocFromDocs(docs, options);
};

export const removeGovtIssuedIdDoc = <T>(
  baseDocId: string,
  docs: T[],
  options: { hasSuffix: boolean; suffixNumber: number },
  matches: (doc: T) => boolean
): { removed: boolean; nextDocs: T[] } | undefined => {
  if (baseDocId !== GOVERNMENT_ISSUED_ID_DOC_ID || !options.hasSuffix) return undefined;

  const idx = options.suffixNumber - 1;
  const current = docs[idx];
  if (!current) return { removed: false, nextDocs: docs };
  if (!matches(current)) return { removed: false, nextDocs: docs };

  const nextDocs = [...docs];
  nextDocs[idx] = null as T;
  return { removed: true, nextDocs };
};

export const storeGovtIssuedIdDoc = <T>(
  baseDocId: string,
  container: Record<string, unknown>,
  key: string,
  doc: T,
  options: { hasSuffix: boolean; suffixNumber: number }
): boolean => {
  if (baseDocId !== GOVERNMENT_ISSUED_ID_DOC_ID) return false;

  const existing = Array.isArray(container[key]) ? (container[key] as T[]) : [];
  const next = [...existing];
  const targetIndex = options.hasSuffix ? (options.suffixNumber - 1) : 0;
  next[targetIndex] = doc;
  container[key] = next;
  return true;
};

const getParticipantCount = (participantsRaw: unknown): number => {
  const entries = parseJsonArray<any>(participantsRaw);
  return Math.max(1, entries.length || 1);
};

const getParticipantRoleLabel = (storageIndex: number): string => {
  if (storageIndex === 0) return 'Main Guarantor';
  return `Shareholder ${storageIndex + 1}`;
};

const getParticipantDisplayName = (entry: any): string => {
  if (!entry) return '';

  const firstName = typeof entry.entity_participant_first_name === 'string' ? entry.entity_participant_first_name : '';
  const lastName = typeof entry.entity_participant_last_name === 'string' ? entry.entity_participant_last_name : '';
  return `${firstName} ${lastName}`.trim();
};

const buildParticipantTitleSuffix = (storageIndex: number, entry: any): string => {
  const role = getParticipantRoleLabel(storageIndex);
  const name = getParticipantDisplayName(entry);
  return name ? `${role} - ${name}` : role;
};

const pickGovtIssuedIdDocFromDocs = <T>(
  docs: T[],
  options: { hasSuffix: boolean; suffixNumber: number }
): T | undefined => {
  if (!Array.isArray(docs) || docs.length === 0) return undefined;
  if (options.hasSuffix) return docs[options.suffixNumber - 1];
  return docs[0];
};

const deriveGovtIssuedIdDocId = (storageIndex: number): string => {
  if (storageIndex === 0) return GOVERNMENT_ISSUED_ID_DOC_ID;
  return `${GOVERNMENT_ISSUED_ID_DOC_ID}__${storageIndex + 1}`;
};

const isGovtIssuedIdDerivedDocId = (docId: string): boolean => {
  return docId === GOVERNMENT_ISSUED_ID_DOC_ID || docId.startsWith(`${GOVERNMENT_ISSUED_ID_DOC_ID}__`);
};
